import { JsonContractError } from "../../../api/http/jsonDecoding";
import {
  parseServerMessage,
  type BrowserToServerMessage,
  type ServerToBrowserMessage,
  type ThreadStateMessage,
} from "../../../api/threads/liveMessages";
import { threadSummaryOf, type EntriesPage } from "../../../api/threads/threadDecoders";
import { reportUiError, toUiError, type UiError } from "../../../errors/uiError";
import {
  INITIAL_LIVE_CONNECTION_STATE,
  liveConnectionWantsSocket,
  transitionLiveConnection,
  type LiveConnectionEvent,
  type LiveConnectionState,
} from "./liveConnection";
import {
  receivedThreadLine,
  receivedPlacementsChange,
  receivedThreadList,
  subscribingProjectList,
  type ProjectThreadList,
} from "../list/projectThreads";
import { EarlierReads } from "./earlierEntries";
import { appendEntries, emptyThreadLog, lastNumberOf, prependEntries, type LogUpdate, type ThreadLog } from "./threadLog";
import { finishedTurns, NO_PROGRESS, retiredTurns, withTurnProgress, withTurnSnapshot, type TurnProgressMap } from "./turnProgress";

/** What the app knows live about one watched Thread. Replaced, never changed, on update. */
export interface LiveThreadSnapshot {
  readonly log: ThreadLog;
  /** The latest `thread_state`, or `null` before the first one. */
  readonly state: ThreadStateMessage | null;
  /** Each open turn's progress, by turn ID: the open turn's and its agent's tool runs' (R3-2). */
  readonly progress: TurnProgressMap;
  /** `refilling`: reading missed entries over HTTP before following again. */
  readonly delivery: "subscribing" | "following" | "refilling" | "failed";
  readonly error: UiError | null;
  readonly loadingEarlier: boolean;
}

export interface ThreadLiveClientOptions {
  readonly socketUrl: string;
  readonly readEntries: (threadId: string, after: number, limit: number) => Promise<EntriesPage>;
  /** The server closed the socket because the session ended (4401). */
  readonly onSessionEnded: () => void;
  readonly createSocket?: (url: string) => WebSocket;
}

const REFILL_PAGE = 200;

interface WatchedThread {
  watchers: number;
  snapshot: LiveThreadSnapshot;
}

type ProjectMessage = Extract<ServerToBrowserMessage, { type: "thread_list" | "thread_listed" | "thread_placements_changed" | "error" }>;

interface WatchedProject {
  watchers: number;
  /** How many of the most recently active Threads the list holds. */
  limit: number;
  list: ProjectThreadList;
}

/**
 * The browser's one live socket, multiplexed over every watched Thread (threads.md T-15)
 * and every followed project's Thread list (Q-62). Each Thread keeps a gap-free log
 * (`threadLog.ts`); a subscription always resumes after the last entry held, and a project
 * subscription gets the whole list again. Writes never go here: they are HTTP calls with
 * idempotency keys.
 * React reads it through `useSyncExternalStore` (`threadLiveContext.ts`).
 */
export class ThreadLiveClient {
  private readonly threads = new Map<string, WatchedThread>();
  private readonly projects = new Map<string, WatchedProject>();
  private readonly listeners = new Set<() => void>();
  private connection: LiveConnectionState = INITIAL_LIVE_CONNECTION_STATE;
  private socket: WebSocket | null = null;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  /** Earlier entries a watcher wants, read into its Thread's log (WD-4). */
  private readonly earlier = new EarlierReads({
    baseOf: (threadId) => this.threads.get(threadId)?.snapshot.log.base ?? null,
    read: (threadId, after, limit) => this.options.readEntries(threadId, after, limit),
    prepend: (threadId, block) => {
      const log = this.threads.get(threadId)?.snapshot.log;
      if (log === undefined) return null;
      this.applyLogUpdate(threadId, prependEntries(log, block), {});
      return this.threads.get(threadId)?.snapshot.log.base ?? null;
    },
    loading: (threadId, loading, error) => { this.update(threadId, error === null ? { loadingEarlier: loading } : { loadingEarlier: loading, error }); },
  });

  constructor(private readonly options: ThreadLiveClientOptions) {}

  /**
   * Follow a Thread. A new one starts after `startAfter`; a watcher that wants earlier entries
   * than the log held, such as the Run view opened beside its graph (WD-4), has them read in.
   * Returns the matching unwatch.
   */
  watch(threadId: string, startAfter: number): () => void {
    const existing = this.threads.get(threadId);
    if (existing !== undefined) {
      existing.watchers += 1;
      if (startAfter < existing.snapshot.log.base) void this.loadEarlier(threadId, startAfter);
    } else {
      this.threads.set(threadId, {
        watchers: 1,
        snapshot: { log: emptyThreadLog(threadId, startAfter), state: null, progress: NO_PROGRESS, delivery: "subscribing", error: null, loadingEarlier: false },
      });
      this.sendSubscribe(threadId);
      this.notify();
    }
    this.dispatch({ type: "wanted" });
    let released = false;
    return () => {
      if (!released) {
        released = true;
        this.release(threadId);
      }
    };
  }

  thread(threadId: string): LiveThreadSnapshot | undefined {
    return this.threads.get(threadId)?.snapshot;
  }

  /** Follow a project's Thread list; returns the matching unwatch. */
  watchProject(projectId: string, limit: number): () => void {
    const existing = this.projects.get(projectId);
    if (existing !== undefined) {
      existing.watchers += 1;
    } else {
      this.projects.set(projectId, { watchers: 1, limit, list: subscribingProjectList(projectId) });
      this.sendProjectSubscribe(projectId);
      this.notify();
    }
    this.dispatch({ type: "wanted" });
    let released = false;
    return () => {
      if (!released) {
        released = true;
        this.releaseProject(projectId);
      }
    };
  }

  project(projectId: string): ProjectThreadList | undefined {
    return this.projects.get(projectId)?.list;
  }

  connectionState(): LiveConnectionState {
    return this.connection;
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /** Read the page of entries before the first one held, or with `from`, every entry after it (`EarlierReads`). */
  loadEarlier(threadId: string, from: number | null = null): Promise<void> {
    return this.earlier.load(threadId, from);
  }

  /** Try to connect at once, for example after signing in again. */
  retryNow(): void {
    if (this.connection.status === "waitingToRetry" || this.connection.status === "sessionEnded" || this.connection.status === "refused") {
      this.dispatch({ type: "retryRequested" });
    }
  }

  /** Close the socket and forget every Thread, for example on sign-out. */
  dispose(): void {
    this.threads.clear();
    this.projects.clear();
    this.dispatch({ type: "unwanted" });
  }

  // ------------------------------------------------------------------ watching

  private release(threadId: string): void {
    const watched = this.threads.get(threadId);
    if (watched === undefined) {
      return;
    }
    watched.watchers -= 1;
    if (watched.watchers > 0) {
      return;
    }
    this.threads.delete(threadId);
    this.send({ type: "unsubscribe", thread_id: threadId });
    this.releaseSocketIfUnused();
    this.notify();
  }

  private releaseProject(projectId: string): void {
    const watched = this.projects.get(projectId);
    if (watched === undefined) {
      return;
    }
    watched.watchers -= 1;
    if (watched.watchers > 0) {
      return;
    }
    this.projects.delete(projectId);
    this.send({ type: "unsubscribe_project", project_id: projectId });
    this.releaseSocketIfUnused();
    this.notify();
  }

  private releaseSocketIfUnused(): void {
    if (this.threads.size === 0 && this.projects.size === 0) {
      this.dispatch({ type: "unwanted" });
    }
  }

  private sendProjectSubscribe(projectId: string): void {
    const watched = this.projects.get(projectId);
    if (watched !== undefined && this.send({ type: "subscribe_project", project_id: projectId, limit: watched.limit })) {
      this.updateProject(projectId, subscribingProjectList(projectId, watched.list));
    }
  }

  private sendSubscribe(threadId: string): void {
    const watched = this.threads.get(threadId);
    if (watched === undefined || watched.snapshot.delivery === "refilling") {
      return;
    }
    if (this.send({ type: "subscribe", thread_id: threadId, after_entry: lastNumberOf(watched.snapshot.log) })) {
      this.update(threadId, { delivery: "subscribing" });
    }
  }

  // ------------------------------------------------------------------ messages

  private receive(text: string): void {
    let message: ServerToBrowserMessage;
    try {
      message = parseServerMessage(text);
    } catch (caught) {
      if (caught instanceof JsonContractError) {
        console.error("A live message breaks the contract; it was ignored.", caught);
        return;
      }
      throw caught;
    }
    if (
      message.type === "thread_list" ||
      message.type === "thread_listed" ||
      message.type === "thread_placements_changed" ||
      (message.type === "error" && typeof message.project_id === "string")
    ) {
      this.receiveProjectMessage(message);
      return;
    }
    if (message.thread_id === null) {
      console.error(`The live socket reported ${message.type === "error" ? message.problem.code : message.type}.`);
      return;
    }
    const watched = this.threads.get(message.thread_id);
    if (watched === undefined || watched.snapshot.delivery === "refilling") {
      return; // Unwatched meanwhile, or being read over HTTP: the refill covers it.
    }
    const threadId = message.thread_id;
    switch (message.type) {
      case "entries": {
        const update = appendEntries(watched.snapshot.log, message.entries);
        this.applyLogUpdate(threadId, update, {
          delivery: "following",
          progress: retiredTurns(watched.snapshot.progress, finishedTurns(message.entries)),
        });
        return;
      }
      case "resync_required":
        void this.refill(threadId, false);
        return;
      case "thread_state": {
        // Module C names the turns open now: the open turn and its agent's tool runs (R3-2).
        const { detail } = message.facts;
        const open = new Set([...(detail.open_turn == null ? [] : [detail.open_turn.turn_id]), ...(detail.open_runs ?? []).map((run) => run.turn_id)]);
        this.update(threadId, { state: message, delivery: "following", progress: retiredTurns(watched.snapshot.progress, new Set(), open) });
        return;
      }
      case "turn_progress":
        this.update(threadId, { progress: withTurnProgress(watched.snapshot.progress, message) });
        return;
      case "turn_snapshot":
        this.update(threadId, { progress: withTurnSnapshot(watched.snapshot.progress, message) });
        return;
      case "error": {
        const error: UiError = { kind: "problem", problem: problemOf(message.problem) };
        reportUiError(error);
        this.update(threadId, { delivery: "failed", error });
        return;
      }
    }
  }

  private receiveProjectMessage(message: ProjectMessage): void {
    const projectId = message.project_id;
    const watched = typeof projectId === "string" ? this.projects.get(projectId) : undefined;
    if (watched === undefined || typeof projectId !== "string") {
      return; // Unfollowed meanwhile.
    }
    switch (message.type) {
      case "thread_list":
        this.updateProject(projectId, receivedThreadList(watched.list, message.threads.map(threadSummaryOf)));
        return;
      case "thread_listed":
        this.updateProject(projectId, receivedThreadLine(watched.list, threadSummaryOf(message.thread)));
        return;
      case "thread_placements_changed":
        this.updateProject(projectId, receivedPlacementsChange(watched.list));
        return;
      case "error":
        reportUiError({ kind: "problem", problem: problemOf(message.problem) });
        this.updateProject(projectId, { ...watched.list, delivery: "failed" });
        return;
    }
  }

  private applyLogUpdate(threadId: string, update: LogUpdate, changes: Partial<LiveThreadSnapshot>): void {
    for (const violation of update.violations) {
      console.error(`Thread ${threadId}: the server broke the order of the log (${violation.kind}).`, violation);
    }
    this.update(threadId, { ...changes, log: update.log });
    if (update.needsRefill) {
      void this.refill(threadId, true);
    }
  }

  /**
   * Read what the browser lacks over HTTP, then follow again after it: after a gap, or
   * when the backlog was too long for the socket (`resync_required`).
   */
  private async refill(threadId: string, stillSubscribed: boolean): Promise<void> {
    if (stillSubscribed) {
      this.send({ type: "unsubscribe", thread_id: threadId });
    }
    this.update(threadId, { delivery: "refilling" });
    try {
      for (;;) {
        const current = this.threads.get(threadId)?.snapshot;
        if (current === undefined) {
          return;
        }
        const after = lastNumberOf(current.log);
        const page = await this.options.readEntries(threadId, after, REFILL_PAGE);
        const latest = this.threads.get(threadId)?.snapshot;
        if (latest === undefined) {
          return;
        }
        const update = appendEntries(latest.log, page.entries);
        // A turn that finished while the socket was away ends its progress here too (Sol design review r0, F6).
        this.applyLogUpdate(threadId, { ...update, needsRefill: false }, { progress: retiredTurns(latest.progress, finishedTurns(page.entries)) });
        const reached = lastNumberOf(update.log);
        if (reached >= page.lastEntryNumber || reached === after) {
          break;
        }
      }
      this.update(threadId, { delivery: "subscribing" });
      this.sendSubscribe(threadId);
    } catch (caught) {
      const error = toUiError(caught);
      reportUiError(error);
      this.update(threadId, { delivery: "failed", error });
    }
  }

  // ------------------------------------------------------------------ socket

  private dispatch(event: LiveConnectionEvent): void {
    const previous = this.connection;
    this.connection = transitionLiveConnection(previous, event);
    if (previous === this.connection) {
      return;
    }
    this.clearRetryTimer();
    if (this.connection.status === "connecting" && previous.status !== "connecting") {
      this.openSocket();
    }
    if (!liveConnectionWantsSocket(this.connection)) {
      this.closeSocket();
    }
    if (this.connection.status === "waitingToRetry") {
      this.retryTimer = setTimeout(() => {
        this.retryTimer = null;
        this.dispatch({ type: "retryDue" });
      }, this.connection.retryInMilliseconds);
    }
    if (this.connection.status === "sessionEnded") {
      this.options.onSessionEnded();
    }
    this.notify();
  }

  private openSocket(): void {
    const socket = (this.options.createSocket ?? ((url) => new WebSocket(url)))(this.options.socketUrl);
    this.socket = socket;
    socket.addEventListener("open", () => {
      if (this.socket !== socket) return;
      this.dispatch({ type: "opened" });
      for (const threadId of this.threads.keys()) {
        this.sendSubscribe(threadId);
      }
      for (const projectId of this.projects.keys()) {
        this.sendProjectSubscribe(projectId);
      }
    });
    socket.addEventListener("message", (event: MessageEvent<unknown>) => {
      if (this.socket === socket && typeof event.data === "string") {
        this.receive(event.data);
      }
    });
    socket.addEventListener("close", (event) => {
      if (this.socket !== socket) return;
      this.socket = null;
      for (const [projectId, watched] of this.projects) {
        // Changes are missed until the list arrives again after the reconnect.
        this.updateProject(projectId, subscribingProjectList(projectId, watched.list));
      }
      this.dispatch({ type: "closed", code: event.code });
    });
  }

  private closeSocket(): void {
    const socket = this.socket;
    this.socket = null;
    if (socket !== null && socket.readyState !== WebSocket.CLOSED) {
      socket.close(1000);
    }
  }

  private send(message: BrowserToServerMessage): boolean {
    if (this.connection.status !== "open" || this.socket?.readyState !== WebSocket.OPEN) {
      return false;
    }
    this.socket.send(JSON.stringify(message));
    return true;
  }

  private clearRetryTimer(): void {
    if (this.retryTimer !== null) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
  }

  private update(threadId: string, changes: Partial<LiveThreadSnapshot>): void {
    const watched = this.threads.get(threadId);
    if (watched === undefined) {
      return;
    }
    watched.snapshot = { ...watched.snapshot, ...changes };
    this.notify();
  }

  private updateProject(projectId: string, list: ProjectThreadList): void {
    const watched = this.projects.get(projectId);
    if (watched === undefined) {
      return;
    }
    watched.list = list;
    this.notify();
  }

  private notify(): void {
    for (const listener of this.listeners) {
      listener();
    }
  }
}

function problemOf(problem: Extract<ServerToBrowserMessage, { type: "error" }>["problem"]) {
  return {
    type: problem.type,
    title: problem.title,
    status: problem.status,
    detail: problem.detail,
    instance: problem.instance,
    code: problem.code,
    requestId: problem.request_id,
    details: problem.details,
  };
}
